import { useMemo } from 'react'
import { Link } from 'gatsby'
import { Image } from 'src/components/ui/Image'
import { Container } from 'src/components/department/Container'
import { InView } from 'react-intersection-observer'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getResponsiveVisibility } from 'src/utils/getResponsiveVisibility'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { ensureHttps } from 'src/utils/stripUrl'
import './pictos.scss'

type Item = {
  image: string
  textColor?: 'preto' | 'branco'
  text: string
  altImage: string
  linkUrl: string
}

type Props = {
  items: Item[]
  parentComponent?: string
  section?: string
  locationOnPage?: string
  enableSession?: boolean
  showOnDesktop?: boolean
  showOnMobile?: boolean
}

const DATA_BANNER_TYPE = 'Categories Shelf'

export function Pictos(props: Props) {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)

  const {
    section,
    locationOnPage,
    items,
    showOnDesktop,
    showOnMobile,
    enableSession,
  } = props

  const visibilityClasses = useMemo(() => {
    return getResponsiveVisibility(showOnDesktop, showOnMobile)
  }, [showOnDesktop, showOnMobile])

  const handleClickLink = (name: string, imageAlt: string, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: name
        ? `${DATA_BANNER_TYPE} | ${name}`
        : imageAlt
        ? `${DATA_BANNER_TYPE} | ${imageAlt}`
        : 'N/A',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })

    if (!window) {
      return
    }

    window.dataLayer = window.dataLayer ?? []

    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'event_track',
      event_category: 'clique',
      event_action: name,
      event_label: 'Pagina de departamento',
    })
  }

  const sendPromoFeatureViewedEvent = (item: Item, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: item?.text
          ? `${DATA_BANNER_TYPE} | ${item?.text}`
          : item?.altImage
          ? `${DATA_BANNER_TYPE} | ${item?.altImage}`
          : 'N/A',
        index,
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  if (!enableSession) return null

  return (
    <Container
      className={`restructure-maxMobile:my-10  restructure-small-desktop:my-[40px] !px-0 ${visibilityClasses}`}
    >
      <ul
        className={`${
          items.length <= 4 && 'lg:justify-center gap-10'
        } pictos flex items-start justify-between  max-w-full overflow-x-scroll scrollbar-hide gap-4 sm:gap-10`}
      >
        {items?.map((item, index) => (
          <InView
            as="li"
            threshold={0.7}
            onChange={(inView) => {
              if (inView) {
                sendPromoFeatureViewedEvent(item, index)
              }
            }}
            triggerOnce
            key={item.text}
            draggable={false}
            data-testid="picto-card"
            className="picto-card"
          >
            <Link
              to={ensureHttps(item.linkUrl)}
              className="flex flex-col justify-center items-center w-[96px] restructure-small-desktop:w-[124px]"
              onClick={() => handleClickLink(item.text, item.altImage, index)}
              aria-label={item.text}
            >
              <span className="w-[64px] h-[64px] restructure-small-desktop:w-[120px] restructure-small-desktop:h-[120px] flex items-center justify-center">
                <Image
                  loading="eager"
                  fetchpriority="high"
                  src={item?.image}
                  height={120}
                  width={120}
                  alt={item?.altImage ?? ''}
                  title={item.text}
                  data-id={item.text}
                  data-banner-type={DATA_BANNER_TYPE}
                />
              </span>
              <h2
                data-testid="picto-text"
                className="text-center font-inter mt-3 text-sm sm:text-base text-restructure-primary"
              >
                {item.text}
              </h2>
            </Link>
          </InView>
        ))}
      </ul>
    </Container>
  )
}
